import config from "./config";
import { getBeHeaders } from './utils/apiHeaders';
import { getAuthorizedFetch } from './utils/fetchRegistry';

/**
 * Conversations service
 *
 * Thin wrapper around the Backend HTTP API for creating, updating, fetching, and
 * searching conversations. This module keeps all networking details in one
 * place so UI components can remain declarative.
 *
 * Conventions
 * - A "conversation" has a name, an array of messages, a sessionId, and a status.
 * - Messages mirror the UI message model: { id, text, type } where type is
 *   either 'sent' (user) or 'received' (assistant/system).
 * - For creates we POST to /conversations.
 */

/**
 * @typedef {('sent'|'received')} MessageType
 */

/**
 * @typedef {Object} ConversationMessage
 * @property {string|number} id - Stable identifier for the message
 * @property {string} text - Plain text or markdown content
 * @property {MessageType} type - UI role: 'sent' (user) or 'received' (assistant/system)
 */

/**
 * @typedef {Object} ConversationRecord
 * @property {string} _id - Unique conversation identifier (MongoDB ObjectId of the sessions_metadata doc)
 * @property {string} conversationName
 * @property {ConversationMessage[]} messages - Loaded on-demand from ADK events (not stored here)
 * @property {string} sessionId
 * @property {string} [userId]
 * @property {string[]} [selectedTools]
 */

/**
 * @typedef {Object} ServiceResult
 * @property {boolean} success
 * @property {string} [message]
 */

/**
 * @typedef {ServiceResult & { conversationId?: string }} SaveResult
 */

/**
 * @typedef {ServiceResult & { conversationName?: string, messages?: ConversationMessage[], sessionId?: string }} FetchResult
 */

/**
 * Conversation Service for managing conversation operations
 */
class ConversationService {
    constructor(config) {
        this.config = config;
        this.userId = null;
    }

    /**
     * Set the authenticated user ID.
     * Must be called once the user has logged in so that conversations are
     * saved and searched scoped to the real user identity.
     * Pass null to clear on logout.
     * @param {string|null} userId
     */
    setUserId(userId) {
        this.userId = userId || null;
    }

    /**
     * Get the current user ID, or null if not authenticated.
     * @returns {string|null}
     */
    getUserId() {
        return this.userId;
    }

    /**
     * Create a new conversation record.
     *
     * Notes
     * - Only the fields used by the UI are sent to the API.
     * - Error body parsing assumes the API returns JSON on non-2xx responses.
     *
     * @param {string|null} conversationId - Ignored (kept for backwards compatibility)
     * @param {string} convName - Human-readable conversation name
     * @param {ConversationMessage[]} chatMessages - Ignored (kept for backwards compatibility)
     * @param {string} sessionId - Backend session id associated to the conversation
     * @param {string[]} selectedTools - List of selected tool names for this conversation
     * @param {string} [appName] - ADK app name (must match the value sent to /run_sse)
     * @param {Object} [metadata] - Extra fields: mode ('compose'|'code'), projectId, projectName, tenantId
     * @returns {Promise<SaveResult>} Operation status and new conversationId on success
     */
    async saveConversation(conversationId, convName, chatMessages, sessionId, selectedTools = [], appName = null, metadata = {}) {
        try {
            const payload = {
                conversationName: convName,
                sessionId: sessionId,
                selectedTools: selectedTools,
                ...(this.userId ? { userId: this.userId } : {}),
                ...(appName ? { appName } : {}),
                ...metadata,
            };

            const response = await getAuthorizedFetch()(`${this.config.agentUrl}/conversations/`, {
                method: 'POST',
                headers: getBeHeaders({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                // Assumes the API returns a JSON error body; if not, this may throw
                const errorBody = await response.json();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorBody?.message || 'Unknown error'}`);
            }

            const result = await response.json();
            return {success: true, message: 'Conversation saved successfully.', conversationId: result._id};
        } catch (error) {
            return {success: false, message: error.message || 'Failed to save conversation.'};
        }
    }

    /**
     * Fetch a single conversation by id.
     *
     * @param {string} conversationId - Target conversation id
     * @returns {Promise<FetchResult>} Full conversation data on success; message on failure
     */
    async fetchConversation(conversationId) {
        try {
            const response = await getAuthorizedFetch()(`${this.config.agentUrl}/conversations/${conversationId}`, {
                headers: getBeHeaders(),
            });

            if (!response.ok) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorBody = await response.json();
                    errorMessage += `, message: ${errorBody?.message || 'Unknown error'}`;
                } catch (_) { /* ignore parse errors on error bodies */ }
                return { success: false, message: errorMessage };
            }

            const data = await response.json();
            return {
                success: true,
                conversationName: data.conversationName,
                messages: data.messages,
                sessionId: data.sessionId,
                selectedTools: data.selectedTools,
                appName: data.appName,
                playbookItemId: data.playbookItemId,
                tenantId: data.tenantId,
            };
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to fetch conversation.'
            };
        }
    }

    /**
     * Update conversation metadata (conversationName, selectedTools).
     *
     * @param {string} conversationId - Target conversation id
     * @param {{ conversationName?: string, selectedTools?: string[] }} updates - Fields to update
     * @returns {Promise<ServiceResult>} Operation status
     */
    async updateConversation(conversationId, updates) {
        try {
            const response = await getAuthorizedFetch()(`${this.config.agentUrl}/conversations/${conversationId}`, {
                method: 'PATCH',
                headers: getBeHeaders({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify(updates),
            });
            if (!response.ok) {
                let msg = `HTTP error! status: ${response.status}`;
                try {
                    const errorBody = await response.json();
                    msg += `, message: ${errorBody?.message || 'Unknown error'}`;
                } catch (_) { /* ignore */ }
                return { success: false, message: msg };
            }
            return { success: true, message: 'Conversation updated successfully.' };
        } catch (error) {
            return { success: false, message: error.message || 'Failed to update conversation.' };
        }
    }

    /**
     * Delete a conversation by id.
     *
     * @param {string} conversationId - Target conversation id
     * @returns {Promise<ServiceResult>} Operation status
     */
    async deleteConversation(conversationId) {
        try {
            const response = await getAuthorizedFetch()(`${this.config.agentUrl}/conversations/${conversationId}`, {
                method: 'DELETE',
                headers: getBeHeaders(),
            });
            if (!response.ok) {
                let msg = `HTTP error! status: ${response.status}`;
                try {
                    const errorBody = await response.json();
                    msg += `, message: ${errorBody?.message || 'Unknown error'}`;
                } catch (_) { /* ignore */ }
                return { success: false, message: msg };
            }
            return { success: true, message: 'Conversation deleted successfully.' };
        } catch (error) {
            return { success: false, message: error.message || 'Failed to delete conversation.' };
        }
    }

    /**
     * Search conversations by name (case-insensitive regex on conversationName).
     *
     * Fetches up to 200 results (_l=200) sorted by updatedAt descending
     * (_s=-updatedAt) so that the most recent conversations are returned first.
     *
     * @param {string} query - Free-text search query
     * @returns {Promise<{success: boolean, results: ConversationRecord[]}>} Matching conversations; empty list on failure
     */
    async searchConversations(query) {
        try {
            const filter = {
                conversationName: {'$regex': query, '$options': 'i'},
                ...(this.userId ? { userId: this.userId } : {}),
            };
            const qParam = encodeURIComponent(JSON.stringify(filter));
            const response = await getAuthorizedFetch()(
                `${this.config.agentUrl}/conversations/?_q=${qParam}&_l=200&_s=-updatedAt`,
                {
                    headers: getBeHeaders(),
                }
            );

            if (!response.ok) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorBody = await response.json();
                    errorMessage += `, message: ${errorBody?.message || 'Unknown error'}`;
                } catch (_) { /* ignore parse errors on error bodies */ }
                // eslint-disable-next-line no-console
                console.error('[ConversationService] searchConversations failed:', errorMessage);
                return { success: false, results: [] };
            }

            const data = await response.json();
            // Guard: the CRUD service should return an array, but validate to be safe
            const results = Array.isArray(data) ? data : (data?.data ?? []);
            return {
                success: true,
                results
            };
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[ConversationService] searchConversations failed:', error.message);
            return {
                success: false,
                results: []
            };
        }
    }
}

// Export singleton instance for convenience
const Conversations = new ConversationService(config);

export default Conversations;
